import React, { useEffect, useState } from "react";
import styled from "styled-components";

const Button = styled.button`
  background-color: #171616;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 15px;
  font-size: 0.9rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
  font-weight: 700;
  height: 3.5rem;

  &:hover {
    background-color: #0f0e0e;
  }
`;

const ButtonTasksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Tasks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Title = styled.div`
  font-size: 1rem;
  margin: 0 0.5rem;
  font-weight: 600;
  color: white;
`;

const ButtonTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  text-align: left;
`;

const ButtonSubtitle = styled.div`
  font-size: 0.8rem;
  color: #16ca16;
  text-align: left;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 0.5rem 0 1rem;
`;

const TabButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  background: ${({ active }) =>
    active ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.05)"};
  color: ${({ active }) => (active ? "#ccff00" : "white")};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: left;
`;

const Name = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: #fff;
`;

const DateText = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  text-align: right;
  font-size: 0.8rem;
`;

const Points = styled.div`
  color: #16ca16;
  font-weight: 600;
`;

const Lifeline = styled.div`
  color: #3b82f6;
  font-weight: 500;
`;

const ButtonWithIcon = ({ name, subtitle, lifetime, date }) => {
  return (
    <Button>
      {/* Left side: Task/Reason + Date */}
      <LeftContent>
        <Name>{name}</Name>
        {date && <DateText>{new Date(date).toLocaleDateString()}</DateText>}
      </LeftContent>

      {/* Right side: Points + Lifeline */}
      <RightContent>
        {subtitle !== undefined && (
          <Points>
            {subtitle > 0 ? "+" : ""}
            {subtitle} XP
          </Points>
        )}
        {lifetime !== undefined && <Lifeline>+{lifetime} hrs</Lifeline>}
      </RightContent>
    </Button>
  );
};

const History = ({ user }) => {
  const [uniqueHistory, setUniqueHistory] = useState<any[]>([]);
  const [pointHistory, setPointHistory] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"tasks" | "points">("tasks");

  useEffect(() => {
    if (user) {
      const combinedHistory = [
        ...(user.completedTasks || []),
        ...(user.taskHistory || []),
      ];

      const uniqueHistoryArray = combinedHistory.reduce((acc: any[], task) => {
        if (!acc.some((t) => t.taskId === task.taskId)) {
          acc.push(task);
        }
        return acc;
      }, []);

      setUniqueHistory(uniqueHistoryArray.reverse());
      setPointHistory(user.pointsHistory || []);
    }
  }, [user]);

  return (
    <ButtonTasksContainer>
      <Title>History</Title>

      {/* Tab Switcher */}
      <TabsContainer>
        <TabButton
          active={activeTab === "tasks"}
          onClick={() => setActiveTab("tasks")}
        >
          Tasks Completed
        </TabButton>
        <TabButton
          active={activeTab === "points"}
          onClick={() => setActiveTab("points")}
        >
          Points Earned
        </TabButton>
      </TabsContainer>

      {/* Content */}
      <Tasks>
        {activeTab === "tasks" &&
          uniqueHistory.map((task, index) => (
            <ButtonWithIcon
              key={index}
              name={task.title || task.description || "Untitled Task"}
              subtitle={task.xpReward || task.rewardXP || 0}
              date={task.completedAt}
              lifetime={
                task.lifelineTime || task.rewardTime || task.timeReward || 0
              }
            />
          ))}

        {activeTab === "points" &&
          pointHistory
            .reverse()
            .map((entry, index) => (
              <ButtonWithIcon
                key={index}
                name={entry.reason}
                subtitle={entry.points}
                lifetime={entry.lifelineTime}
                date={entry.date}
              />
            ))}
      </Tasks>
    </ButtonTasksContainer>
  );
};

export default History;
